import { faAdd, faArchive, faClone, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Stack } from "react-bootstrap";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import IconButton from "../../ui/IconButton";
import { NoteType } from "../../../types/notetype";
import { deleteNoteType, fetchNoteTypeFields, patchNoteType, postNoteType, postNoteTypeField } from "../../../services/httpClient";
import NoteTypeCardField from "./NoteTypeCardField";
import SelectObject from "../../ui/SelectObject";
import Textarea from "../../ui/Textarea";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import fieldTypes from "../../../data/notetypefieldtypes.json";

interface FieldType {
  key: string;
  name: string;
}
interface NoteTypeState {
  type: NoteType;
  setErrMessage: any;
  getNotes: any;
  setShowArchived: any;
  setShowDefault: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  isDefault: boolean;
}

const NoteTypeCard = ({
  type,
  setErrMessage,
  getNotes,
  setShowArchived,
  setShowDefault,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  isDefault,
}: NoteTypeState) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isUnpublishing, setIsUnpublishing] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const [isUnarchiving, setIsUnarchiving] = useState<boolean>(false);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [noteName, setNoteName] = useState<string>();
  const [typeFields, setTypeFields] = useState<any[]>();
  const [allowSave, setAllowSave] = useState<boolean>(false);
  const [allowPublish, setAllowPublish] = useState<boolean>(true);
  const [newLabel, setNewLabel] = useState<string>("");
  const [newType, setNewType] = useState<string>("");
  const [showAddField, setShowAddField] = useState<boolean>(false);
  const [enableAddField, setEnableAddField] = useState<boolean>(false);
  const [numPreviews, setNumPreviews] = useState<number>(0);
  const typesList: FieldType[] = fieldTypes;

  useEffect(() => {
    const emptyDropdown = type?.fields.some((f: any) => f.type === "dropdown" && f.options.length === 0);
    setAllowPublish(!emptyDropdown);
    setNoteName(type?.name);
    setTypeFields(type?.fields);
  }, [type]);

  const onClickShowAddField = () => {
    setNewLabel("");
    setNewType("");
    setShowAddField(true);
  };

  const updateLabel = (e: any) => {
    setNewLabel(e.target.value);

    if (e.target.value && newType) {
      setEnableAddField(true);
    } else {
      setEnableAddField(false);
    }
  };

  const updateType = (e: any) => {
    setNewType(e.target.value);

    if (newLabel && e.target.value && e.target.value !== "0") {
      setEnableAddField(true);
    } else {
      setEnableAddField(false);
    }
  };

  const getNoteTypeFields = () => {
    fetchNoteTypeFields(type?.id)
      .then((res: any) => {
        const data = res[0].data;
        const previewCount = data.filter((d: any) => d.preview).length;
        setTypeFields(data);
        setNumPreviews(previewCount);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickAddFieldSubmit = (e: any) => {
    e?.preventDefault();
    onClickAddField();
  };

  const onClickAddField = () => {
    const newField: any = {
      label: newLabel,
      type: newType,
    };

    setErrMessage("");

    postNoteTypeField(newField, type?.id)
      .then((res: any) => {
        setEnableAddField(false);
        setShowAddField(false);
        setNewLabel("");
        setNewType("");
        unsavedChanges.value = false;
        setOrgToSave(false);
        getNotes(false);
        setShowToast(true);
        setToastText("Note Type Field Created");
        setToastBody(newLabel);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancelField = () => {
    setNewLabel("");
    setNewType("");
    setShowAddField(false);
  };

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickSave = (showPublished: boolean) => {
    let updatedNoteType: any;
    if (isDefault) {
      updatedNoteType = {
        name: noteName,
        is_published: type?.is_published,
        notetype_id: type?.id,
      };
    } else {
      updatedNoteType = {
        name: noteName,
      };
    }

    setErrMessage("");

    patchNoteType(updatedNoteType, type?.id)
      .then((res: any) => {
        setIsEditing(false);
        setAllowSave(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getNotes(showPublished);
        setShowToast(true);
        setToastText("Note Type Updated");
        setToastBody(noteName);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancel = () => {
    setErrMessage("");
    setNoteName(type?.name);
    setAllowSave(false);
    setIsEditing(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickNoteSubmit = (e: any) => {
    e?.preventDefault();
    onClickSave(false);
  };

  const updateNoteName = (e: any) => {
    setNoteName(e.target.value);
    setAllowSave(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickPublish = () => {
    setIsPublishing(true);
  };

  const onClickUnpublish = () => {
    setIsUnpublishing(true);
  };

  const onClickCancelUnpublish = () => {
    setIsUnpublishing(false);
  };

  const onClickConfirmUnpublish = () => {
    const updatedNoteType = {
      name: noteName,
      is_published: 0,
      notetype_id: type?.id,
    };

    patchNoteType(updatedNoteType, type?.id)
      .then((res: any) => {
        setIsEditing(false);
        setIsUnpublishing(false);
        setAllowSave(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getNotes(true);
        setShowToast(true);
        setToastText("Note Type Unpublished");
        setToastBody(noteName);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancelPublish = () => {
    setIsPublishing(false);
  };

  const onClickConfirmPublish = (org_id: any) => {
    let publishNoteType;
    if (org_id) {
      publishNoteType = {
        is_published: true,
      };
    } else {
      publishNoteType = {
        notetype_id: type?.id,
        is_published: true,
      };
    }

    setErrMessage("");

    patchNoteType(publishNoteType, type?.id)
      .then((res: any) => {
        setIsPublishing(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "publish_custom_note_type");
        getNotes(true);
        setShowToast(true);
        setToastText("Note Type Published");
        setToastBody(noteName);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickArchive = () => {
    setIsArchiving(true);
  };

  const onClickCancelArchive = () => {
    setIsArchiving(false);
  };

  const onClickConfirmArchive = () => {
    const archiveNoteType = {
      is_archived: true,
    };

    setErrMessage("");

    patchNoteType(archiveNoteType, type?.id)
      .then((res: any) => {
        setIsArchiving(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "archive_custom_note_type");
        getNotes(true);
        setShowToast(true);
        setToastText("Note Type Archived");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickUnarchive = () => {
    setIsUnarchiving(true);
  };

  const onClickCancelUnarchive = () => {
    setIsUnarchiving(false);
  };

  const onClickConfirmUnarchive = () => {
    const unarchiveNoteType = {
      is_archived: false,
    };

    setErrMessage("");

    patchNoteType(unarchiveNoteType, type?.id)
      .then((res: any) => {
        setIsUnarchiving(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "unarchive_custom_note_type");
        setErrMessage("");
        setShowArchived(false);
        getNotes(true);
        setShowToast(true);
        setToastText("Note Type Published");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickDuplicate = () => {
    setIsDuplicating(true);
  };

  const onClickCancelDuplicate = () => {
    setIsDuplicating(false);
  };

  const onClickConfirmDuplicate = () => {
    //filter out date field
    const dupFields = type?.fields.filter((f: any) => f.key !== "date");

    const duplicateNoteType: NoteType = {
      name: type?.name + " - Copy",
      type: "custom",
      is_duplicate: true,
      fields: dupFields.map((f: any, index: number) => ({
        label: f?.label,
        type: f?.type,
        order: index + 1,
      })),
    };

    setErrMessage("");

    postNoteType(duplicateNoteType)
      .then((res: any) => {
        setIsDuplicating(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "duplicate_custom_note_type");
        setErrMessage("");
        setShowArchived(false);
        getNotes(false);
        setShowToast(true);
        setToastText("Note Type Duplicated");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setIsDuplicating(false);
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  const onClickConfirmDelete = () => {
    setErrMessage("");
    if (type?.id) {
      deleteNoteType(type?.id)
        .then((res: any) => {
          setIsDeleting(false);
          unsavedChanges.value = false;
          setOrgToSave(false);
          // trackFeatureEvent("button_click", "delete_custom_note_type");
          getNotes(false);
          setShowToast(true);
          setToastText("Note Type Deleted");
          setToastBody(type?.name);
        })
        .catch((err: any) => {
          setErrMessage(err?.response?.data?.msg);
        });
    }
  };

  const renderPublish = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to publish - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmPublish(type?.organization_id)}>
              Confirm Publish
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelPublish()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderUnpublish = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>
                Are you sure you want to {isDefault ? "archive" : "unpublish"} - {type?.name}?
              </Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmUnpublish()}>
              Confirm {isDefault ? "Archive" : "Unpublish"}
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelUnpublish()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderArchive = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to archive - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmArchive()}>
              Confirm Archive
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelArchive()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderUnarchive = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to publish and remove from archive - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmUnarchive()}>
              Confirm Publish
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelUnarchive()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to delete - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickConfirmDelete()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDuplicate = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to create duplicate copy in drafts - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmDuplicate()}>
              Confirm Duplicate
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDuplicate()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {isPublishing ? (
        renderPublish()
      ) : isUnpublishing ? (
        renderUnpublish()
      ) : isArchiving ? (
        renderArchive()
      ) : isUnarchiving ? (
        renderUnarchive()
      ) : isDuplicating ? (
        renderDuplicate()
      ) : isDeleting ? (
        renderDelete()
      ) : (
        <Card className="bg-light mb-3" key={type?.id}>
          <Card.Body>
            <Row>
              <Col xs={12}>
                {isEditing ? (
                  <Form onSubmit={(e: any) => onClickNoteSubmit(e)}>
                    <Stack direction="horizontal" gap={3} className="mb-3">
                      <Stack style={{ justifyContent: "center" }} className="col-6">
                        <Input
                          label="Note Type Name"
                          type="text"
                          value={noteName}
                          onUpdate={(e: any) => updateNoteName(e)}
                          hasFocus={true}
                        />
                      </Stack>
                      <Button
                        className="button app-secondary-color button"
                        onClick={() => onClickSave(type?.is_published ? true : false)}
                        disabled={!allowSave}
                      >
                        Save
                      </Button>
                      <div className="vr my-2" />
                      <Button className="button app-primary-color button" onClick={() => onClickCancel()}>
                        Cancel
                      </Button>
                    </Stack>
                  </Form>
                ) : (
                  <Stack direction="horizontal" gap={3}>
                    <Stack style={{ justifyContent: "center" }} className="col-6">
                      <h5 className="app-orange-text ms-3">{type.name}</h5>
                    </Stack>
                    {isDefault ? (
                      type?.is_published ? (
                        <Button className="button app-primary-color me-3" onClick={() => onClickUnpublish()}>
                          Archive
                        </Button>
                      ) : (
                        <Button className="button app-tertiary-color me-3" onClick={() => onClickPublish()}>
                          Publish
                        </Button>
                      )
                    ) : (
                      <>
                        {!type?.is_archived &&
                          (type?.is_published ? (
                            <>
                              <IconButton onClickButton={() => onClickEdit()} tooltipText="Edit Name" icon={faEdit} />
                              <IconButton onClickButton={() => onClickArchive()} tooltipText="Archive" icon={faArchive} />
                            </>
                          ) : (
                            <>
                              <IconButton onClickButton={() => onClickEdit()} tooltipText="Edit Name" icon={faEdit} />
                              <IconButton onClickButton={() => onClickDelete()} tooltipText="Delete" icon={faTrash} />
                            </>
                          ))}
                        <div className="me-3">
                          <IconButton onClickButton={() => onClickDuplicate()} tooltipText="Duplicate" icon={faClone} />
                        </div>
                      </>
                    )}
                  </Stack>
                )}
              </Col>
            </Row>
            {!isDefault && (
              <>
                <Row>
                  <h6 className="ms-3">Fields</h6>
                </Row>
                <Row className="mb-2 g-3">
                  <Col xs={6}>
                    <Input label="Label" type="text" value={"Household Member"} disabled={true} onUpdate={null} />
                  </Col>
                  <Col xs={6}>
                    <Input label="Type" type="text" value={"dropdown"} disabled={true} onUpdate={null} />
                  </Col>
                </Row>
                <Row className="mb-2 g-3">
                  <Col>
                    <Input label="Label" type="text" value={"Date"} disabled={true} onUpdate={null} />
                  </Col>
                  <Col>
                    <Input label="Type" type="text" value={"date"} disabled={true} onUpdate={null} />
                  </Col>
                </Row>
              </>
            )}
            {!isDefault &&
              typeFields
                ?.slice(0, -1)
                .map((t: any, index: number) => (
                  <NoteTypeCardField
                    key={t?.id}
                    typeId={type?.id}
                    field={t}
                    lastField={index === type?.fields.length - 1}
                    getNotes={getNotes}
                    getNoteTypeFields={getNoteTypeFields}
                    archived={type?.is_archived}
                    published={type.is_published}
                    setOrgToSave={setOrgToSave}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                    setErrMessage={setErrMessage}
                    setNumPreviews={setNumPreviews}
                    numPreviews={numPreviews}
                  />
                ))}
            {showAddField && (
              <Form onSubmit={(e: any) => onClickAddFieldSubmit(e)}>
                <Row className="g-3 my-1">
                  <Col xs={6}>
                    <Textarea
                      label="New Field Label"
                      value={newLabel}
                      onUpdate={(e: any) => updateLabel(e)}
                      rows={1}
                      css={""}
                      hasFocus={noteName ? true : false}
                    />
                  </Col>
                  <Col>
                    <SelectObject label="Type" value={newType} options={typesList} onUpdate={(e: any) => updateType(e)} />
                  </Col>
                  <Col xs={"auto"} className="d-flex align-items-center">
                    <Button type="submit" className="app-secondary-color button me-3" disabled={!enableAddField}>
                      Add
                    </Button>
                    <div className="vr my-2" />
                    <Button className="app-primary-color button ms-3" onClick={() => onClickCancelField()}>
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
            {type?.is_archived ? (
              <Row className="mt-3">
                <Col className="text-end">
                  <Button className="button app-tertiary-color" onClick={() => onClickUnarchive()}>
                    Publish
                  </Button>
                </Col>
              </Row>
            ) : (
              !type?.is_published &&
              !isDefault && (
                <Row className="mt-3">
                  <Col className="text-start align-content-end">
                    <IconEmbeddedButton onClickButton={() => onClickShowAddField()} tooltipText="Add Field" icon={faAdd} />
                  </Col>
                  <Col xs={2} className="text-end">
                    <Button className="button app-tertiary-color" onClick={() => onClickPublish()} disabled={!allowPublish}>
                      Publish
                    </Button>
                  </Col>
                </Row>
              )
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default NoteTypeCard;
